import React from 'react';
import { Helmet } from 'react-helmet';

const NeomologyCalculators = () => {
  return (
    <div className="min-h-screen bg-gradient-to-r from-blue-100 to-indigo-100">
      <Helmet>
        <title>Neomology Calculators | Comprehensive Naming & Linguistic Tools</title>
        <meta name="description" content="Collection of 45+ neomology calculators for analyzing names, linguistic patterns, historical naming conventions, and cultural naming practices." />
        <meta name="keywords" content="neomology calculators, name analysis calculators, linguistic tools, onomastics calculators, naming convention calculators" />
        <meta property="og:title" content="Neomology Calculators | Comprehensive Naming & Linguistic Tools" />
        <meta property="og:description" content="Collection of 45+ neomology calculators for analyzing names, linguistic patterns, historical naming conventions, and cultural naming practices." />
        <meta property="og:image" content="/social-preview.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Neomology Calculators | Comprehensive Naming & Linguistic Tools" />
        <meta name="twitter:description" content="Collection of 45+ neomology calculators for analyzing names, linguistic patterns, historical naming conventions, and cultural naming practices." />
        <link rel="canonical" href="https://yourdomain.com/neomology-calculators" />
      </Helmet>

      {/* WhatsApp Support Button */}
      <div className="fixed bottom-6 right-6">
        <a 
          href="https://api.whatsapp.com/send?phone=YOUR_PHONE_NUMBER&text=I%20need%20help%20with%20the%20Neomology%20Calculators" 
          target="_blank" 
          rel="noopener noreferrer"
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-full shadow-lg transition-all transform hover:scale-105"
        >
          <span className="text-2xl">💬</span>
          <span className="sr-only">Contact us on WhatsApp</span>
        </a>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="header-icon text-4xl text-blue-600 mb-4">🔤</div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Neomology Calculators</h1>
          <p className="calculator-count text-xl text-gray-600">45+ calculators</p>
        </div>

        <div className="max-w-2xl mx-auto mb-12">
          <div className="calculator-description bg-white p-6 rounded-lg shadow-md">
            <p className="text-gray-600 mb-4">
              Explore our specialized collection of neomology calculators designed to help analyze names, linguistic patterns, historical naming conventions, and cultural naming practices. These tools provide valuable insights for linguists, historians, and anyone interested in the science of naming.
            </p>
            <button className="read-more inline-block bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors">
              Read more
            </button>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Name Analysis <span className="emoji ml-2">🔍</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/name-origin" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Name Origin Calculator</h3>
                <p className="text-gray-600">Determine the historical origins of names</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/name-meaning" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Name Meaning Calculator</h3>
                <p className="text-gray-600">Analyze the meanings behind names</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/name-popularity" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Name Popularity Calculator</h3>
                <p className="text-gray-600">Track the popularity of names over time</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/name-compatibility" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Name Compatibility Calculator</h3>
                <p className="text-gray-600">Analyze compatibility between names</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/name-phonetics" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Name Phonetics Calculator</h3>
                <p className="text-gray-600">Analyze the sound patterns of names</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/name-variants" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Name Variants Calculator</h3>
                <p className="text-gray-600">Find variations and spellings of names</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Linguistic Analysis <span className="emoji ml-2">📊</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/morpheme-analysis" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Morpheme Analysis Calculator</h3>
                <p className="text-gray-600">Break down words into morphemes</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/syllable-counter" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Syllable Counter Calculator</h3>
                <p className="text-gray-600">Count syllables in words and names</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/phoneme-frequency" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Phoneme Frequency Calculator</h3>
                <p className="text-gray-600">Analyze frequency of phonemes in names</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/onomatopoeia-analysis" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Onomatopoeia Analysis Calculator</h3>
                <p className="text-gray-600">Analyze sound-imitating words</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/semantic-change" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Semantic Change Calculator</h3>
                <p className="text-gray-600">Track changes in word meanings over time</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/etymology" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Etymology Calculator</h3>
                <p className="text-gray-600">Trace the historical development of words</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Cultural Naming <span className="emoji ml-2">🌍</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/cultural-name-origins" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Cultural Name Origins Calculator</h3>
                <p className="text-gray-600">Explore naming traditions across cultures</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/religious-naming" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Religious Naming Calculator</h3>
                <p className="text-gray-600">Analyze names with religious significance</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/royal-naming" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Royal Naming Calculator</h3>
                <p className="text-gray-600">Track naming patterns in royal families</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/ethnic-name-identification" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Ethnic Name Identification Calculator</h3>
                <p className="text-gray-600">Identify ethnic origins of names</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/mythological-names" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Mythological Names Calculator</h3>
                <p className="text-gray-600">Explore names from world mythology</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/historical-naming" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Historical Naming Calculator</h3>
                <p className="text-gray-600">Analyze naming trends throughout history</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Naming Conventions <span className="emoji ml-2">📝</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/scientific-naming" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Scientific Naming Calculator</h3>
                <p className="text-gray-600">Analyze binomial nomenclature</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/technical-naming" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Technical Naming Calculator</h3>
                <p className="text-gray-600">Analyze naming conventions in technical fields</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/brand-naming" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Brand Naming Calculator</h3>
                <p className="text-gray-600">Analyze effectiveness of brand names</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/geographical-naming" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Geographical Naming Calculator</h3>
                <p className="text-gray-600">Analyze names of places and locations</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/taxonomic-naming" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Taxonomic Naming Calculator</h3>
                <p className="text-gray-600">Analyze biological classification names</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/legal-naming" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Legal Naming Calculator</h3>
                <p className="text-gray-600">Analyze legal name changes and documentation</p>
              </a>
            </div>
          </div> <br /><br />
          <div className="numerology-section mb-12">
  <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
    General Numerology Calculators <span className="emoji ml-2">🔢</span>
  </h2>
  <div className="numerology-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    <div className="numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/life-path-number" className="numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Life Path Number Calculator</h3>
        <p className="text-gray-600">Calculate your Life Path number</p>
      </a>
    </div>
    <div className="numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/destiny-number" className="numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Destiny Number Calculator</h3>
        <p className="text-gray-600">Calculate your Destiny number</p>
      </a>
    </div>
    <div className="numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/soul-urge-number" className="numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Soul Urge Number (Heart’s Desire) Calculator</h3>
        <p className="text-gray-600">Calculate your Soul Urge number</p>
      </a>
    </div>
    <div className="numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/personality-number" className="numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Personality Number Calculator</h3>
        <p className="text-gray-600">Calculate your Personality number</p>
      </a>
    </div>
    <div className="numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/expression-number" className="numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Expression Number Calculator</h3>
        <p className="text-gray-600">Calculate your Expression number</p>
      </a>
    </div>
    <div className="numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/birthday-number" className="numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Birthday Number Calculator</h3>
        <p className="text-gray-600">Calculate your Birthday number</p>
      </a>
    </div>
    <div className="numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/maturity-number" className="numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Maturity Number Calculator</h3>
        <p className="text-gray-600">Calculate your Maturity number</p>
      </a>
    </div>
    <div className="numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/hidden-passion-number" className="numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Hidden Passion Number Calculator</h3>
        <p className="text-gray-600">Calculate your Hidden Passion number</p>
      </a>
    </div>
    <div className="numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/karmic-debt-number" className="numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Karmic Debt Number Calculator</h3>
        <p className="text-gray-600">Calculate your Karmic Debt number</p>
      </a>
    </div>
    <div className="numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/karmic-lesson-number" className="numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Karmic Lesson Number Calculator</h3>
        <p className="text-gray-600">Calculate your Karmic Lesson number</p>
      </a>
    </div>
  </div>
</div>

<div className="advanced-numerology-section mb-12">
  <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
    Advanced Numerology Calculators <span className="emoji ml-2">🔮</span>
  </h2>
  <div className="advanced-numerology-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    <div className="advanced-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/pinnacle-number" className="advanced-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Pinnacle Number Calculator</h3>
        <p className="text-gray-600">Calculate your Pinnacle number</p>
      </a>
    </div>
    <div className="advanced-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/challenge-number" className="advanced-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Challenge Number Calculator</h3>
        <p className="text-gray-600">Calculate your Challenge number</p>
      </a>
    </div>
    <div className="advanced-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/attainment-number" className="advanced-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Attainment Number Calculator</h3>
        <p className="text-gray-600">Calculate your Attainment number</p>
      </a>
    </div>
    <div className="advanced-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/balance-number" className="advanced-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Balance Number Calculator</h3>
        <p className="text-gray-600">Calculate your Balance number</p>
      </a>
    </div>
    <div className="advanced-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/personal-year-number" className="advanced-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Personal Year Number Calculator</h3>
        <p className="text-gray-600">Calculate your Personal Year number</p>
      </a>
    </div>
    <div className="advanced-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/personal-month-number" className="advanced-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Personal Month Number Calculator</h3>
        <p className="text-gray-600">Calculate your Personal Month number</p>
      </a>
    </div>
    <div className="advanced-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/personal-day-number" className="advanced-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Personal Day Number Calculator</h3>
        <p className="text-gray-600">Calculate your Personal Day number</p>
      </a>
    </div>
    <div className="advanced-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/essence-number" className="advanced-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Essence Number Calculator</h3>
        <p className="text-gray-600">Calculate your Essence number</p>
      </a>
    </div>
    <div className="advanced-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/bridge-number" className="advanced-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Bridge Number Calculator</h3>
        <p className="text-gray-600">Calculate your Bridge number</p>
      </a>
    </div>
    <div className="advanced-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/rational-thought-number" className="advanced-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Rational Thought Number Calculator</h3>
        <p className="text-gray-600">Calculate your Rational Thought number</p>
      </a>
    </div>
  </div>
</div>

<div className="name-based-numerology-section mb-12">
  <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
    Name-Based Numerology Calculators <span className="emoji ml-2">🔤</span>
  </h2>
  <div className="name-based-numerology-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    <div className="name-based-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/name-vibrations" className="name-based-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Name Vibrations Calculator</h3>
        <p className="text-gray-600">Analyze the vibrational energy of your name</p>
      </a>
    </div>
    <div className="name-based-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/business-name" className="name-based-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Business Name Numerology Calculator</h3>
        <p className="text-gray-600">Analyze the numerology of your business name</p>
      </a>
    </div>
    <div className="name-based-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/baby-name" className="name-based-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Baby Name Numerology Calculator</h3>
        <p className="text-gray-600">Analyze the numerology of a baby name</p>
      </a>
    </div>
    <div className="name-based-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/name-initial-power" className="name-based-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Name Initial Power Calculator</h3>
        <p className="text-gray-600">Calculate the power of your name initials</p>
      </a>
    </div>
    <div className="name-based-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/full-name-compatibility" className="name-based-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Full Name Numerology Compatibility Calculator</h3>
        <p className="text-gray-600">Check the compatibility of full names</p>
      </a>
    </div>
  </div>
</div>


<div className="astro-numerology-section mb-12">
  <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
    Astro-Numerology Calculators <span className="emoji ml-2">🔮</span>
  </h2>
  <div className="astro-numerology-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    <div className="astro-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/zodiac-numerology" className="astro-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Zodiac Numerology Calculator</h3>
        <p className="text-gray-600">Analyze your numerology based on your zodiac sign</p>
      </a>
    </div>
    <div className="astro-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/chinese-numerology" className="astro-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Chinese Numerology Calculator</h3>
        <p className="text-gray-600">Analyze your numerology based on Chinese astrology</p>
      </a>
    </div>
    <div className="astro-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/chaldean-numerology" className="astro-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Chaldean Numerology Calculator</h3>
        <p className="text-gray-600">Analyze your numerology with the Chaldean system</p>
      </a>
    </div>
    <div className="astro-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/pythagorean-numerology" className="astro-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Pythagorean Numerology Calculator</h3>
        <p className="text-gray-600">Analyze your numerology based on the Pythagorean method</p>
      </a>
    </div>
    <div className="astro-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/kabbalah-numerology" className="astro-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Kabbalah Numerology Calculator</h3>
        <p className="text-gray-600">Analyze your numerology with the Kabbalah system</p>
      </a>
    </div>
  </div>
</div>


<div className="relationship-love-numerology-section mb-12">
  <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
    Relationship & Love Numerology Calculators <span className="emoji ml-2">💖</span>
  </h2>
  <div className="relationship-love-numerology-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    <div className="relationship-love-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/love-compatibility" className="relationship-love-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Love Compatibility Numerology Calculator</h3>
        <p className="text-gray-600">Analyze love compatibility between two people</p>
      </a>
    </div>
    <div className="relationship-love-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/marriage-compatibility" className="relationship-love-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Marriage Compatibility Numerology Calculator</h3>
        <p className="text-gray-600">Analyze marriage compatibility between partners</p>
      </a>
    </div>
    <div className="relationship-love-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/twin-flame-number" className="relationship-love-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Twin Flame Number Calculator</h3>
        <p className="text-gray-600">Discover your Twin Flame number</p>
      </a>
    </div>
    <div className="relationship-love-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/soulmate-number" className="relationship-love-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Soulmate Number Calculator</h3>
        <p className="text-gray-600">Discover your Soulmate number</p>
      </a>
    </div>
  </div>
</div>


<div className="career-finance-numerology-section mb-12">
  <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
    Career & Finance Numerology Calculators <span className="emoji ml-2">💼</span>
  </h2>
  <div className="career-finance-numerology-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    <div className="career-finance-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/money-number" className="career-finance-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Money Number Calculator</h3>
        <p className="text-gray-600">Discover your money number and financial potential</p>
      </a>
    </div>
    <div className="career-finance-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/career-path" className="career-finance-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Career Path Numerology Calculator</h3>
        <p className="text-gray-600">Analyze your ideal career path based on numerology</p>
      </a>
    </div>
    <div className="career-finance-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/success-number" className="career-finance-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Success Number Calculator</h3>
        <p className="text-gray-600">Discover your success number and how to achieve success</p>
      </a>
    </div>
    <div className="career-finance-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/wealth-number" className="career-finance-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Wealth Number Calculator</h3>
        <p className="text-gray-600">Analyze your wealth potential using numerology</p>
      </a>
    </div>
  </div>
</div>


<div className="health-wellbeing-numerology-section mb-12">
  <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
    Health & Well-being Numerology Calculators <span className="emoji ml-2">💪</span>
  </h2>
  <div className="health-wellbeing-numerology-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    <div className="health-wellbeing-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/health-number" className="health-wellbeing-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Health Number Calculator</h3>
        <p className="text-gray-600">Discover your health number and well-being insights</p>
      </a>
    </div>
    <div className="health-wellbeing-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/energy-number" className="health-wellbeing-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Energy Number Calculator</h3>
        <p className="text-gray-600">Calculate your energy number for vitality and wellness</p>
      </a>
    </div>
  </div>
</div>


<div className="spiritual-numerology-section mb-12">
  <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
    Spiritual Numerology Calculators <span className="emoji ml-2">✨</span>
  </h2>
  <div className="spiritual-numerology-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    <div className="spiritual-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/angel-number" className="spiritual-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Angel Number Calculator</h3>
        <p className="text-gray-600">Discover the meaning of your angel numbers</p>
      </a>
    </div>
    <div className="spiritual-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/master-number" className="spiritual-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Master Number Calculator (11, 22, 33, etc.)</h3>
        <p className="text-gray-600">Explore the significance of master numbers</p>
      </a>
    </div>
    <div className="spiritual-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/past-life-number" className="spiritual-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Past Life Number Calculator</h3>
        <p className="text-gray-600">Reveal insights from your past life through numerology</p>
      </a>
    </div>
    <div className="spiritual-numerology-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <a href="/calculator/soul-evolution-number" className="spiritual-numerology-link block">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Soul Evolution Number Calculator</h3>
        <p className="text-gray-600">Discover your soul evolution path and purpose</p>
      </a>
    </div>
  </div>
</div>


        </div>
      </div>
    </div>
  );
};

export default NeomologyCalculators;